/*
  Product card frame preview.
  Old workflow: two uploaded mockups per product (black-framed Ortala as the
  card image, black-framed Tam Oturt as the hover image). New workflow uploads
  a single frameless Tam Oturt image, so this script synthesizes both card
  states live: black frame + cream mat in light mode, white frame + cream mat
  in dark mode (passepartout always matches the shipped product); hover fills
  the print (Tam Oturt).
*/
(() => {
  'use strict';

  const TR_MAP = { ç: 'c', ğ: 'g', ı: 'i', İ: 'i', ö: 'o', ş: 's', ü: 'u' };
  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[çğıİöşü]/g, (ch) => TR_MAP[ch] || ch)
      .replace(/\s+/g, ' ')
      .trim();

  const FRAME_LIGHT = '#141416';
  const FRAME_DARK = '#ececec';

  const isDarkScheme = () => {
    const scheme = document.documentElement.getAttribute('data-scheme') || 'light';
    if (scheme === 'dark') return true;
    if (scheme === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  const frameBase = () => (isDarkScheme() ? FRAME_DARK : FRAME_LIGHT);

  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  };

  const shade = (hex, f) => {
    const [r, g, b] = hexToRgb(hex).map((c) =>
      Math.max(0, Math.min(255, Math.round(c * f + (f > 1 ? (f - 1) * 40 : 0))))
    );
    return `rgb(${r}, ${g}, ${b})`;
  };

  const matRenk = () =>
    (window.AlaPaspas && window.AlaPaspas.kagitRenk) || '#f4f1ea';

  const paintFrame = (frame) => {
    const base = frameBase();
    frame.style.borderTopColor = shade(base, 1.5);
    frame.style.borderLeftColor = shade(base, 1.2);
    frame.style.borderRightColor = shade(base, 0.9);
    frame.style.borderBottomColor = shade(base, 0.62);
    frame.style.background = matRenk();
  };

  const repaintAll = () => {
    document.querySelectorAll('.cfp-frame').forEach(paintFrame);
  };

  // Only synthesize for system products: the card image is a frameless mockup.
  const isFramelessAlt = (alt) => norm(alt).includes('cercevesiz');

  /* Ortala 2cm: AlaPaspas.olcek; yoksa aynı formül (KENAR_CM=2 / 21). */
  const matOlcek = () => {
    const o = window.AlaPaspas && window.AlaPaspas.olcek;
    return typeof o === 'number' && o > 0 && o < 1 ? o : 1 - 2 * (2 / 21);
  };

  /* Per image only — never Promise.all / imagesLoaded the grid. */
  const whenReady = (img, fn) => {
    if (img.dataset.cfpBound === '1') return;
    img.dataset.cfpBound = '1';
    const run = () => {
      if (img.dataset.cfpRan === '1') return;
      img.dataset.cfpRan = '1';
      img.classList.add('cfp-in');
      fn();
    };
    /*
      Native lazy images often report complete=true with naturalWidth=0
      before they actually fetch (esp. inside visibility:hidden FOUC
      gates, horizontal overflow, or pinned translate). Treating that
      as ready made layout() abort and never listen for the real load.
    */
    if (img.complete && img.naturalWidth) {
      run();
      return;
    }
    img.addEventListener('load', run, { once: true });
    img.addEventListener('error', run, { once: true });
  };

  const mediaOf = (img) => img.closest('.set-card-member') || img.closest('.media');

  const memberRatio = (el) => {
    const cs = getComputedStyle(el);
    const w = parseFloat(cs.getPropertyValue('--member-w')) || 0;
    const h = parseFloat(cs.getPropertyValue('--member-h')) || 0;
    if (w > 0 && h > 0) return w / h;
    const img = el.querySelector('img.cfp-src, img.media--first, img:not(.cfp-art)');
    if (img && img.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
    return 1;
  };

  const layoutSetRow = (row) => {
    if (!row) return;
    const members = [...row.querySelectorAll(':scope > .set-card-member')];
    if (!members.length) return;
    const styles = getComputedStyle(row);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const padX = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
    const padY = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
    const availW = Math.max(0, row.clientWidth - padX);
    const availH = Math.max(0, row.clientHeight - padY);
    if (!availW || !availH) return;
    const ratios = members.map(memberRatio);
    const sum = ratios.reduce((a, b) => a + b, 0);
    if (!sum) return;
    const fillH = (availW - gap * Math.max(0, members.length - 1)) / sum;
    const sharedH = Math.min(availH, fillH);
    if (sharedH < 4) return;
    members.forEach((m, i) => {
      /* !important: duvar-set.css has its own !important height formula on
         .set-card-member.media (container-query based, meant as a
         CSS-only fallback) which was silently winning over this plain
         inline style — CSS spec gives any !important stylesheet rule
         priority over a non-important inline style regardless of
         selector specificity. That formula does not reliably resolve to
         the true available row width, so 3- and 4-piece sets kept
         overflowing their card on mobile even after the row-fit math
         here was correct. Setting the inline style itself as !important
         makes this JS-measured value (real row.clientWidth, always
         accurate) win unconditionally, on every breakpoint. */
      m.style.setProperty('height', `${sharedH.toFixed(2)}px`, 'important');
      m.style.setProperty('width', `${(sharedH * ratios[i]).toFixed(2)}px`, 'important');
    });
  };

  const observeSetRow = (row) => {
    if (!row || row.dataset.cfpRowObs || typeof ResizeObserver === 'undefined') return;
    row.dataset.cfpRowObs = 'true';
    new ResizeObserver(() => layoutSetRow(row)).observe(row);
  };

  const build = (media, img) => {
    if (!media || media.dataset.cfp) return;
    const isSetMember = media.classList.contains('set-card-member');
    if (isSetMember) {
      const row = media.closest('.set-card-members');
      observeSetRow(row);
      layoutSetRow(row);
    }
    media.dataset.cfp = 'true';
    const cropTop =
      media.classList.contains('cfp-crop-top') ||
      media.getAttribute('data-crop') === 'top' ||
      !!(media.closest && media.closest('.cfp-crop-top, [data-crop="top"]'));
    /*
      Keep the source print visible until reveal(). Hiding it here (or via
      :has(> .cfp)) left Monet Seçkisi as an empty cream plate while .cfp
      was still opacity:0. Ghost is avoided because .cfp stays hidden
      until cfp-active.
    */
    media.classList.add('cfp-pending');

    const lockContain = !!(
      media.classList.contains('cfp-lock-contain') ||
      media.getAttribute('data-fit-lock') === 'contain' ||
      (media.closest && media.closest('.cfp-lock-contain, [data-fit-lock="contain"]'))
    );
    const wrap = document.createElement('div');
    wrap.className = isSetMember ? 'cfp cfp--tam-oturt' : 'cfp';
    if (lockContain && !isSetMember) {
      wrap.classList.add('cfp-lock-contain');
      wrap.setAttribute('data-fit-lock', 'contain');
    }
    if (cropTop) {
      wrap.classList.add('cfp-crop-top');
      wrap.setAttribute('data-crop', 'top');
    }
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.setProperty(
      '--ala-paspas-olcek',
      isSetMember ? '100%' : `${(matOlcek() * 100).toFixed(6)}%`
    );
    wrap.style.setProperty('--ala-paspas-renk', matRenk());

    const frame = document.createElement('div');
    frame.className = 'cfp-frame';
    paintFrame(frame);

    const paper = document.createElement('div');
    paper.className = 'cfp-paper';
    paper.style.background = matRenk();

    const art = document.createElement('img');
    art.className = 'cfp-art';
    art.alt = '';
    art.draggable = false;
    /*
      Baski kopyasi, kartta zaten yuklenen gorselin aynisi. Bu yuzden lazy
      beklemeye gerek yok: adres onbellekten geliyor, eager olunca cerceve
      gorsel hazir olur olmaz tek seferde belirir.
    */
    art.loading = img.getAttribute('loading') === 'lazy' ? 'lazy' : 'eager';
    art.decoding = 'async';

    paper.appendChild(art);
    frame.appendChild(paper);
    wrap.appendChild(frame);
    media.appendChild(wrap);

    const boxSize = () => {
      const rect = media.getBoundingClientRect();
      const w = media.clientWidth || media.offsetWidth || Math.round(rect.width);
      const h = media.clientHeight || media.offsetHeight || Math.round(rect.height);
      return { w, h };
    };
    const printRatio = () => {
      if (art.naturalWidth && art.naturalHeight) return art.naturalWidth / art.naturalHeight;
      if (img.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
      return 0;
    };
    const layout = () => {
      const { w: W, h: H } = boxSize();
      const ratio = printRatio();
      if (!W || !H || !ratio) return;
      if (isSetMember) {
        const fw = W * 0.98;
        const fh = H * 0.98;
        const lip = Math.max(3, Math.min(fw, fh) * 0.05);
        frame.style.width = `${fw.toFixed(1)}px`;
        frame.style.height = `${fh.toFixed(1)}px`;
        frame.style.borderWidth = `${lip.toFixed(1)}px`;
        frame.style.setProperty('--cfp-lip', `${lip.toFixed(1)}px`);
        return;
      }
      const availW = W * 0.98;
      const availH = H * 0.98;
      let fw = Math.min(availW, availH * ratio);
      let fh = fw / ratio;
      const lip = Math.max(3, Math.min(fw, fh) * 0.05);
      const iw = Math.min(availW - 2 * lip, (availH - 2 * lip) * ratio);
      const ih = iw / ratio;
      fw = iw + 2 * lip;
      fh = ih + 2 * lip;
      frame.style.width = `${fw.toFixed(1)}px`;
      frame.style.height = `${fh.toFixed(1)}px`;
      frame.style.borderWidth = `${lip.toFixed(1)}px`;
      frame.style.setProperty('--cfp-lip', `${lip.toFixed(1)}px`);
    };

    /*
      Reveal this card when THIS print is ready. Do not wait for sibling
      cards, the rest of the grid, or decode() of every image.
    */
    let revealTries = 0;
    const hideSource = () => {
      img.style.setProperty('opacity', '0', 'important');
      img.style.setProperty('visibility', 'hidden', 'important');
    };
    const showSrcFallback = () => {
      if (media.classList.contains('cfp-active')) return;
      media.classList.add('cfp-src-fallback');
      media.classList.remove('cfp-pending');
      wrap.classList.remove('cfp-ready');
      img.style.removeProperty('opacity');
      img.style.removeProperty('visibility');
    };
    const reveal = () => {
      if (media.classList.contains('cfp-active')) return;
      if (!frame.style.width) {
        layout();
        if (!frame.style.width && revealTries < 24) {
          revealTries += 1;
          requestAnimationFrame(reveal);
          return;
        }
        if (!frame.style.width) return;
      }
      /* Never flash an empty cream plate as the "final" card. */
      if (!art.naturalWidth) return;
      hideSource();
      media.classList.add('cfp-active');
      media.classList.remove('cfp-pending', 'cfp-src-fallback');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => wrap.classList.add('cfp-ready'));
      });
    };

    const sync = () => {
      const srcset = img.getAttribute('srcset');
      const boxW = media.clientWidth || media.offsetWidth || 0;
      /*
        Perf: kopya, ASIL kartın sizes değerini birebir kullanır. Böylece
        tarayıcı srcset'ten aynı adayı seçer ve dosya önbellekten gelir —
        ikinci (çoğu zaman daha büyük, ör. 80vw@DPR3→900w) bir indirme
        yapılmaz. Görüntü kalitesi asıl kartla birebir aynıdır.

        Asıl kartta sizes yoksa eski davranış: kutu genişliği; kutu 0 ise
        80vw (0 sizes hiç aday seçtirmiyordu — çerçeveli kağıt, baskı yok).
      */
      const origSizes = img.getAttribute('sizes');
      const sizes = origSizes || (boxW > 8 ? `${Math.ceil(boxW)}px` : '80vw');
      if (srcset && art.getAttribute('srcset') !== srcset) art.setAttribute('srcset', srcset);
      if (art.getAttribute('sizes') !== sizes) art.setAttribute('sizes', sizes);
      const src = img.currentSrc || '';
      if (src && art.getAttribute('src') !== src) art.src = src;
      layout();
      if (isSetMember) {
        const row = media.closest('.set-card-members');
        layoutSetRow(row);
        layout();
      }
      if (art.complete && art.naturalWidth) reveal();
      else art.addEventListener('load', () => { layout(); reveal(); }, { once: true });
    };

    if (img.complete && img.naturalWidth) sync();
    else img.addEventListener('load', sync);
    window.setTimeout(() => {
      if (media.classList.contains('cfp-active')) return;
      layout();
      if (frame.style.width && art.naturalWidth) {
        reveal();
        return;
      }
      showSrcFallback();
    }, 600);
    const onBox = () => {
      layout();
      if (art.complete && art.naturalWidth) reveal();
    };
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(onBox).observe(media);
    } else {
      window.addEventListener('resize', onBox);
    }
  };

  let cardObserver = null;
  const getCardObserver = () => {
    if (cardObserver) return cardObserver;
    if (typeof IntersectionObserver === 'undefined') return null;

    cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const media = entry.target;
            cardObserver.unobserve(media);
            if (media.dataset.cfp) return;
            const img = media._cfpImg || media.querySelector('img.cfp-src, img.media--first, img:not(.cfp-art)');
            if (img && !media.dataset.cfp) {
              img.classList.add('cfp-in');
              enqueueBuild(media, img);
            }
          }
        });
      },
      { rootMargin: '200px 400px 200px 400px', threshold: 0 }
    );
    return cardObserver;
  };

  const scan = (root) => {
    const context = root && root.querySelectorAll ? root : document;
    const obs = getCardObserver();
    const vh = window.innerHeight || document.documentElement.clientHeight || 800;

    context
      .querySelectorAll('a.media img, .media.media--hover-effect img, .set-card-member img, img.cfp-src')
      .forEach((img) => {
        if (img.classList.contains('cfp-art')) return;
        const flagged = img.classList.contains('cfp-src');
        if (!flagged && !isFramelessAlt(img.getAttribute('alt'))) return;
        const media = mediaOf(img);
        if (!media) return;

        /*
          Fallback every CFP surface — not only homepage secki.
          Monet could recover while Van Gogh / Çok Satanlar stayed cream
          because collection-slider was outside the old closest() list.
        */
        if (!media.dataset.cfpWatch) {
          const startWatch = () => {
            if (media.dataset.cfpWatch === '1') return;
            if (!(img.currentSrc || img.getAttribute('src'))) return;
            media.dataset.cfpWatch = '1';
            window.setTimeout(() => {
              if (
                media.classList.contains('cfp-active') ||
                media.classList.contains('cfp-src-fallback')
              ) {
                return;
              }
              media.classList.add('cfp-src-fallback');
              media.classList.remove('cfp-pending');
              img.style.removeProperty('opacity');
              img.style.removeProperty('visibility');
            }, 700);
          };
          if (img.currentSrc || img.getAttribute('src')) startWatch();
          else img.addEventListener('load', startWatch, { once: true });
        }

        whenReady(img, () => {
          if (media.dataset.cfp) return;

          if (!obs) {
            enqueueBuild(media, img);
            return;
          }

          const rect = media.getBoundingClientRect();
          const inBand =
            rect.top < vh + 400 &&
            rect.bottom > -200 &&
            rect.left < (window.innerWidth || 400) + 800 &&
            rect.right > -200;
          if (inBand) {
            enqueueBuild(media, img);
          } else if (!media.dataset.cfpObserved) {
            media.dataset.cfpObserved = 'true';
            media._cfpImg = img;
            obs.observe(media);
          }
        });
      });
  };

  const buildQueue = [];
  let pumping = false;
  const enqueueBuild = (media, img) => {
    if (!media || media.dataset.cfp || media.dataset.cfpQueued) return;
    media.dataset.cfpQueued = '1';
    buildQueue.push([media, img]);
    if (pumping) return;
    pumping = true;
    const tick = () => {
      const n = Math.min(2, buildQueue.length);
      for (let i = 0; i < n; i++) {
        const item = buildQueue.shift();
        if (item && item[0] && !item[0].dataset.cfp) build(item[0], item[1]);
      }
      if (buildQueue.length) requestAnimationFrame(tick);
      else pumping = false;
    };
    requestAnimationFrame(tick);
  };

  let scheduled = false;
  const scheduleScan = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      bindScrollers(document);
      scan(document);
    });
  };

  const bindScrollers = (root) => {
    const context = root && root.querySelectorAll ? root : document;
    context
      .querySelectorAll('.scroll-products__products, .popular-products .swiper-wrapper, .popular-products .swiper, .recently-viewed__swiper, .recently-viewed__wrapper')
      .forEach((el) => {
        if (el.dataset.cfpScroll) return;
        el.dataset.cfpScroll = '1';
        el.addEventListener('scroll', scheduleScan, { passive: true });
      });
  };

  const start = () => {
    document.querySelectorAll('.set-card-members').forEach((row) => {
      observeSetRow(row);
      layoutSetRow(row);
    });
    bindScrollers(document);
    scan(document);
    new MutationObserver(scheduleScan).observe(document.body, {
      childList: true,
      subtree: true,
    });

    new MutationObserver(repaintAll).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-scheme'],
    });

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    if (mq.addEventListener) mq.addEventListener('change', repaintAll);
    else if (mq.addListener) mq.addListener(repaintAll);
  };

  window.AlaCFP = {
    scan: (root) => {
      bindScrollers(root);
      scan(root);
    },
    scheduleScan,
    build: enqueueBuild,
    repaintAll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
